import React, { useMemo, useState } from 'react';
import { FlightCardWidget, type FlightCardPayload } from './FlightCardWidget';
import type { FlightFilters } from '../types/filterSidebar';
import { filterFlights, selectForTab, type FlightTab } from '../utilities/flightFilters';
import styles from './FlightContainerWidget.module.scss';

type FlightListPayload = {
  title: string;
  items: FlightCardPayload[];
  return_items?: FlightCardPayload[];
  direction?: string;
  emphasis_offer_id?: string;
  total_count: number;
  currency: string;
};

const TABS: FlightTab[] = ['All', 'Best Value', 'Cheapest', 'Fastest'];

export function FlightContainerWidget({
  payload,
  onAction,
  filters,
}: {
  payload: FlightListPayload;
  onAction?: (actionId: string, value?: Record<string, unknown>) => void;
  /** The sidebar's flight filters. Applied to every tab; the tab the
   * traveller is on stays selected while they change. */
  filters?: FlightFilters;
}) {
  const [activeTab, setActiveTab] = useState<FlightTab>('Best Value');
  const [selectedOutboundId, setSelectedOutboundId] = useState<string | null>(null);
  const [selectedReturnId, setSelectedReturnId] = useState<string | null>(null);
  const outboundSource = payload.items || [];
  const inboundSource = payload.return_items || [];
  // Outbound and return are shown together, side by side, whenever the
  // search came back with return flights at all.
  const twoColumns = inboundSource.length > 0;

  const outboundItems = useMemo(
    () => selectForTab(filterFlights(outboundSource, filters), activeTab),
    [outboundSource, filters, activeTab],
  );
  const inboundItems = useMemo(
    () => selectForTab(filterFlights(inboundSource, filters), activeTab),
    [inboundSource, filters, activeTab],
  );
  const noMatches = outboundSource.length > 0 && outboundItems.length === 0;

  const selectedOutbound = outboundSource.find((item) => item.offer_id === selectedOutboundId) || null;

  // A round trip's details need the chosen outbound alongside the return
  // offer being viewed; Chat.tsx merges the two.
  const handleAction = (actionId: string, value?: Record<string, unknown>) => {
    if (twoColumns && selectedOutbound && actionId.startsWith('view_offer:')) {
      onAction?.(actionId, { ...value, outbound_offer: selectedOutbound });
      return;
    }
    onAction?.(actionId, value);
  };

  return (
    <section className={styles.root} aria-label={payload.title || 'Flights'}>
      <div className={styles.titleRow}>
        <h3 className={styles.title}>{twoColumns ? 'Round trip flights' : payload.title}</h3>
        <span className={styles.chip}>
          {outboundItems.length === outboundSource.length
            ? `${payload.total_count} offers`
            : `${outboundItems.length} of ${outboundSource.length} offers`}
          {payload.currency ? ` · ${payload.currency}` : ''}
        </span>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Sort flights">
        {TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            className={`${styles.tab}${activeTab === tab ? ` ${styles.tabActive}` : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab}
          </button>
        ))}
      </div>

      {noMatches ? (
        <p className={styles.empty}>No flights match the current filters. Adjust them in the sidebar.</p>
      ) : twoColumns ? (
        <div className={styles.columns}>
          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Outbound flights</h3>
            <div className={styles.list} role="listbox" aria-label="Outbound flights">
              {outboundItems.map((item) => (
                <FlightCardWidget
                  key={item.offer_id}
                  payload={{
                    ...item,
                    ai_adjustments:
                      item.offer_id === payload.emphasis_offer_id
                        ? { emphasis: 'cheapest' }
                        : item.ai_adjustments,
                  }}
                  selected={item.offer_id === selectedOutboundId}
                  onSelect={() => setSelectedOutboundId(item.offer_id)}
                  showView={false}
                />
              ))}
            </div>
          </div>

          <div className={styles.column}>
            <h3 className={styles.columnTitle}>Return flights</h3>
            <div className={styles.list} role="listbox" aria-label="Return flights">
              {inboundItems.map((item) => (
                <FlightCardWidget
                  key={item.offer_id}
                  payload={item}
                  selected={item.offer_id === selectedReturnId}
                  onSelect={() => setSelectedReturnId(item.offer_id)}
                  onAction={handleAction}
                  viewDisabled={!selectedOutbound}
                  viewDisabledReason="Select an outbound flight first"
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className={styles.list} role="listbox" aria-label="Flights">
          {outboundItems.map((item) => (
            <FlightCardWidget
              key={item.offer_id}
              payload={{
                ...item,
                ai_adjustments:
                  item.offer_id === payload.emphasis_offer_id
                    ? { emphasis: 'cheapest' }
                    : item.ai_adjustments,
              }}
              selected={item.offer_id === selectedOutboundId}
              onSelect={() => setSelectedOutboundId(item.offer_id)}
              onAction={handleAction}
            />
          ))}
        </div>
      )}
    </section>
  );
}
